import type {
  DeliveryCoords,
  FulfillmentType,
  Order,
  OrderPaymentMethod,
  OrderStatus,
  PodStatus,
} from '@shopgnd/shared';
import { normalizeCartLines } from '@shopgnd/shared';

function parseJsonField<T>(value: unknown): T | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return value as T;
}

/** Map AppSync / JSON order row to shared Order type. */
export function mapAmplifyOrder(row: Record<string, unknown>): Order {
  return {
    id: String(row.id),
    buyerUserId: String(row.buyerUserId),
    vendorId: String(row.vendorId),
    lines: normalizeCartLines(row.lines),
    totalCents: Number(row.totalCents),
    currency: String(row.currency ?? 'XCD'),
    status: row.status as OrderStatus,
    stripePaymentIntentId: row.stripePaymentIntentId
      ? String(row.stripePaymentIntentId)
      : undefined,
    trackingNumber: row.trackingNumber ? String(row.trackingNumber) : undefined,
    deliveryParish: row.deliveryParish ? String(row.deliveryParish) : undefined,
    deliveryNote: row.deliveryNote ? String(row.deliveryNote) : undefined,
    deliveryCoords: parseJsonField<DeliveryCoords>(row.deliveryCoords),
    invoiceNumber: row.invoiceNumber ? String(row.invoiceNumber) : undefined,
    checkoutId: row.checkoutId ? String(row.checkoutId) : undefined,
    paymentExpiresAt: row.paymentExpiresAt ? String(row.paymentExpiresAt) : undefined,
    paymentMethod: row.paymentMethod
      ? (String(row.paymentMethod) as OrderPaymentMethod)
      : undefined,
    fulfillmentType: row.fulfillmentType
      ? (String(row.fulfillmentType) as FulfillmentType)
      : undefined,
    deliveryFeeCents: row.deliveryFeeCents != null ? Number(row.deliveryFeeCents) : undefined,
    convenienceFeeCents:
      row.convenienceFeeCents != null ? Number(row.convenienceFeeCents) : undefined,
    platformFeeCents: row.platformFeeCents != null ? Number(row.platformFeeCents) : undefined,
    vendorPayoutCents: row.vendorPayoutCents != null ? Number(row.vendorPayoutCents) : undefined,
    procurementRequested: row.procurementRequested === true,
    podStatus: row.podStatus ? (String(row.podStatus) as PodStatus) : undefined,
    reviewPromptSentAt: row.reviewPromptSentAt ? String(row.reviewPromptSentAt) : undefined,
    createdAt: String(row.createdAt ?? new Date().toISOString()),
    updatedAt: String(row.updatedAt ?? new Date().toISOString()),
  };
}

/** Map DynamoDB AttributeValue map (plain strings from Lambda) to Order. */
export function mapDynamoOrderItem(item: Record<string, string | undefined>): Order {
  const linesRaw = item.lines;
  let lines: unknown = linesRaw;
  if (linesRaw) {
    try {
      lines = JSON.parse(linesRaw);
    } catch {
      lines = [];
    }
  }
  return mapAmplifyOrder({
    ...item,
    lines,
    totalCents: item.totalCents ? Number(item.totalCents) : 0,
    deliveryFeeCents: item.deliveryFeeCents ? Number(item.deliveryFeeCents) : undefined,
    convenienceFeeCents: item.convenienceFeeCents ? Number(item.convenienceFeeCents) : undefined,
    platformFeeCents: item.platformFeeCents ? Number(item.platformFeeCents) : undefined,
    vendorPayoutCents: item.vendorPayoutCents ? Number(item.vendorPayoutCents) : undefined,
    procurementRequested: item.procurementRequested === 'true',
  });
}
